import { useState, useMemo, useRef, useLayoutEffect, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/AuthContext';
import UserList from './UserList';
import UserDetailPanel from './UserDetailPanel';
import FloatingUserButton from './FloatingUserButton';
import UserFilters from './UserFilters';
import './UsersMainView.scss';

const SCROLL_STORAGE_KEY = 'usersListScrollPosition';
const SEARCH_STORAGE_KEY = 'usersListSearchTerm';
const ROLE_STORAGE_KEY = 'usersListSelectedRole';

export default function UsersMainView({ users, loading, error, onUserUpdated, onUserAdded, onUserDeleted }) {
  const { t } = useTranslation();
  const { userRole } = useAuthContext();
  const { userId } = useParams();
  const navigate = useNavigate();
  const listScrollRef = useRef(null);

  // Filter states - initialize from sessionStorage
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem(SEARCH_STORAGE_KEY) || '';
  });
  const [selectedRole, setSelectedRole] = useState(() => {
    const stored = sessionStorage.getItem(ROLE_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  });

  // Save scroll position on scroll - use sessionStorage to survive component remounts
  const handleListScroll = (e) => {
    const position = e.target.scrollTop;
    sessionStorage.setItem(SCROLL_STORAGE_KEY, position.toString());
  };

  // Save filters to sessionStorage when they change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    sessionStorage.setItem(SEARCH_STORAGE_KEY, value);
  };

  const handleRoleChange = (value) => {
    setSelectedRole(value);
    if (value === null) {
      sessionStorage.removeItem(ROLE_STORAGE_KEY);
    } else {
      sessionStorage.setItem(ROLE_STORAGE_KEY, value.toString());
    }
  };

  useEffect(() => {
    const ur = Number(userRole);
    if (ur === 2 && selectedRole === 1) {
      handleRoleChange(null);
    }
    if (ur === 5 && selectedRole !== null && selectedRole !== 5) {
      handleRoleChange(null);
    }
    if (ur === 4 && selectedRole !== null && ![4, 5].includes(selectedRole)) {
      handleRoleChange(null);
    }
    if (ur === 3 && selectedRole !== null && selectedRole !== 3) {
      handleRoleChange(null);
    }
  }, [userRole, selectedRole]);

  // Restore scroll position after DOM update and React has finished rendering
  useLayoutEffect(() => {
    const container = listScrollRef.current;
    const savedPosition = parseInt(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '0', 10);

    if (container && savedPosition > 0) {
      container.scrollTop = savedPosition;

      setTimeout(() => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      }, 0);

      const timeoutId = setTimeout(() => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [userId]);

  const handleUserClick = (user) => {
    navigate(`/dashboard/users/${user.id}`);
  };

  const handleBackToList = () => {
    navigate('/dashboard/users');
  };

  // Filter users based on search term and role
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Role filter
      if (selectedRole !== null) {
        const r = user.role || user.groups?.[0];
        if (r !== selectedRole) {
          return false;
        }
      }

      // Search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const firstName = (user.first_name || '').toLowerCase();
        const lastName = (user.last_name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();

        return fullName.includes(search) || email.includes(search);
      }

      return true;
    });
  }, [users, searchTerm, selectedRole]);

  return (
    <div className={`users-main-view ${userId ? 'has-user-selected' : ''}`}>
      {!loading && !error && users.length > 0 && (
        <UserFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedRole={selectedRole}
          onRoleChange={handleRoleChange}
        />
      )}

      <div className="users-master-detail-container">
        {/* Master - User List */}
          <div className="users-master">
            <div className="users-list-container" ref={listScrollRef} onScroll={handleListScroll}>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">{t('ui_loading')}</span>
                  </div>
                  <p className="mt-2">{t('users_loading_list')}</p>
                </div>
              ) : error ? (
                <div style={{
                  color: '#b71c1c',
                  backgroundColor: '#fde0e0',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">{t('users_empty')}</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">{t('users_empty_filters')}</p>
                  <button
                    className="btn btn-link"
                    onClick={() => {
                      handleSearchChange('');
                      handleRoleChange(null);
                    }}
                  >
                    {t('users_clear_filters')}
                  </button>
                </div>
              ) : (
                <UserList
                  users={filteredUsers}
                  selectedUserId={userId}
                  onUserClick={handleUserClick}
                />
              )}
            </div>
          </div>

        {/* Detail - User Details */}
        <div className={`users-detail ${userId ? 'show-detail' : ''}`}>
          {!userId ? (
            <div className="user-detail-placeholder">
              <i className="fas fa-hand-pointer" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }} />
              <h3>{t('users_placeholder_title')}</h3>
              <p>{t('users_placeholder_hint')}</p>
            </div>
          ) : (
            <div className="panel-wrapper" key={userId}>
              <UserDetailPanel
                userId={userId}
                onBack={handleBackToList}
                onUserUpdated={onUserUpdated}
                onUserDeleted={onUserDeleted}
              />
            </div>
          )}
        </div>
      </div>

      <FloatingUserButton onUserAdded={onUserAdded} />
    </div>
  );
}
